import { useTranslation } from "react-i18next";
import useSWR, { mutate } from "swr";
import { DelayedSpinner } from "../../DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../Error";
import type { Assets, PIMProduct } from "../../../types/types.PIM";
import type { DataMutate, WithPagination } from "../../../types/types";
import { EditAssetForm } from "../../../pages/SharedPages/ProductDetailPage/Assets/EditAssetForm";
import { endpoints } from "../../../endpoints";
import { useStoreState } from "../../../util/util";

type EditDocumentToAttributeProps = {
  assetId: string;
  product?: PIMProduct;
  onComplete: () => void;
  onCancel: () => void;
  hideAccessibility?: boolean;
};

const stubProduct = {
  status: "archived",
  id: "",
} as PIMProduct;

export function EditDocumentAttribute({
  assetId,
  product,
  onComplete,
  onCancel,
  hideAccessibility = false,
}: EditDocumentToAttributeProps) {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const assetEndpoint = endpoints.v2_tenants_id_pim_assets_id(
    tenant_id,
    assetId
  );

  const { data: asset, error } = useSWR<Assets>(assetEndpoint, {
    revalidateOnFocus: false,
  });

  const refreshAssetData: DataMutate<WithPagination<{ data: Assets[] }>> =
    async () => {
      await mutate(assetEndpoint);
      return undefined;
    };

  if (error) {
    return (
      <ErrorPlaceholder
        message={t("Could not load document. Please try again later.")}
      />
    );
  }

  if (!asset) {
    return <DelayedSpinner />;
  }

  return (
    <EditAssetForm
      formType="Documents"
      assetData={asset}
      product={product ?? stubProduct}
      fetchProductData={refreshAssetData}
      onComplete={() => onComplete()}
      onCancel={onCancel}
      hideAccessibility={hideAccessibility}
      allowUpload={true}
      isProductContext={Boolean(product?.id)}
    />
  );
}
